import React from "react";
import moment from "moment";
import Badge from "@dtci/spellbook/Badge";

/**
 * "Until Window" badge for the promo grid.
 *
 *   - Future window (today < startDate)   → grey badge with the day count
 *   - In window     (startDate ≤ today ≤ endDate) → blue badge with "0"
 *   - Past window   (today > endDate)     → green badge with "—"
 *
 * Renders nothing when both dates are missing — operator sees an empty cell
 * instead of a misleading badge.
 */
export const computeWindowState = (startDate, endDate, now = new Date()) => {
  const today = moment(now).startOf("day");
  const start = startDate ? moment(startDate).startOf("day") : null;
  const end = endDate ? moment(endDate).startOf("day") : null;

  if (!start && !end) return null;

  if (end && today.isAfter(end)) {
    // Negative `days` (= -daysSinceClose) so sorting orders past windows from
    // most-recently-closed down to long-ago-closed; the rendered label is "—".
    const daysSinceClose = today.diff(end, "days");
    return {
      state: "past",
      days: -daysSinceClose,
      label: "—",
      color: "success",
    };
  }
  if (start && today.isBefore(start)) {
    const days = start.diff(today, "days");
    return { state: "future", days, label: String(days), color: "base" };
  }
  // In-window covers: today within [start, end], or only one date present.
  // `active` is Spellbook's indigo/blue palette — it's not in the TS types but
  // the CSS supports it (.sb-badge_active in Badge.css). Closer to the design
  // ask of "blue while within the window" than `primary`, which is the brand
  // purple in this theme.
  return { state: "inWindow", days: 0, label: "0", color: "active" };
};

const TITLE_BY_STATE = {
  future: (days) => `Window opens in ${days} day${days === 1 ? "" : "s"}`,
  inWindow: () => "Window is open",
  past: () => "Window has closed",
};

const WindowCountdownCellRenderer = ({ data }) => {
  const result = computeWindowState(data?.startDate, data?.endDate);
  if (!result) return null;
  return (
    <Badge
      light
      color={result.color}
      title={TITLE_BY_STATE[result.state](result.days)}
    >
      {result.label}
    </Badge>
  );
};

export default WindowCountdownCellRenderer;
